import { DeleteOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import { HeySynclyMessageDisplay } from '@/entities/hey-syncly';
import DashboardCardStyles from '@/features/Dashboard/components/styles/DashboardCardStyles';
import {
  DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY,
  DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY,
} from '@/features/Dashboard/types';
import { radius } from '@/styles/sizes';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import Input from '../../../../components/Inputs/Input';
import useReportCardDrag from '../../hooks/useReportCardDrag';
import useReportStore from '../../store/ReportStore';
import type { ReportColumnProps } from '../ReportEditor/ReportColumn';

const { StyledDragArea, StyledDashboardCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

const ReportAnalysisItem = (props: ReportColumnProps) => {
  const chatMessageContainerRef = useRef<HTMLDivElement>(null);
  const removeColumn = useReportStore((state) => state.removeColumn);
  const setDragging = useReportStore((state) => state.setDragging);
  const mode = useReportStore((state) => state.mode);
  const updateItem = useReportStore((state) => state.updateItem);

  const isReportEditMode = mode === 'EDIT';
  const [titleEditMode, setTitleEditMode] = useState(false);

  const { index, column, width, report } = props;

  const analysis = column.item.analysis;

  if (!analysis) throw new Error('analysis is not exist');

  const { dragRef, previewRef, isDragging } = useReportCardDrag({
    index,
    setSomeDragging: setDragging,
    column,
  });

  const chartItem = report.items?.find(
    (item) => item.chart?.alias === analysis.reference_id,
  );

  const onLinkedChartClick = () => {
    const chartAlias = chartItem?.chart?.alias;
    if (!chartAlias) return;
    const element = document.querySelector(
      `[${DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY}="${chartAlias}"]`,
    );
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('focus');
    setTimeout(() => {
      element.classList.remove('focus');
    }, 3000);
  };

  useEffect(() => {
    if (!isReportEditMode) {
      setTitleEditMode(false);
    }
  }, [isReportEditMode]);

  if (!analysis) return null;
  return (
    <StyledDashboardCardWrapper
      ref={previewRef}
      isDragging={isDragging}
      width={width}
      style={{ height: '100%' }}
    >
      <StyledDashboardCard
        {...{
          [DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY]: analysis.alias,
        }}
      >
        {isReportEditMode && (
          <StyledDragArea
            ref={dragRef}
            className="drag-area-handler"
            style={{ top: 26 }}
          >
            <HandlerIcon />
          </StyledDragArea>
        )}

        <Transition
          status={true}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Style>
            <DashboardCardHeader>
              <DashboardCardHeaderReferenceSection
                g={4}
                onClick={onLinkedChartClick}
                a="center"
              >
                <LinkOutlined />
                <Typo type="label4" weight="r" ellipsis>
                  Smart brief for
                </Typo>
                <Typo type="label4" weight="sb" ellipsis>
                  {chartItem?.chart?.name}
                </Typo>
              </DashboardCardHeaderReferenceSection>
              <Flex f={1} j="space-between" a="center" padding="0 24px">
                <TooltipV2 tooltipNode="This content is generated by AI. It will be updated periodically.">
                  <Flex g={8} f={1} a="center">
                    <AIIcon />
                    {titleEditMode ? (
                      <Input
                        style={{ maxWidth: 400 }}
                        sizeType="l"
                        defaultValue={analysis.title}
                        onBlur={(e) => {
                          const value = e.currentTarget.value;
                          updateItem(column.item.alias, {
                            analysis: {
                              ...analysis,
                              title: value,
                            },
                          });
                          setTitleEditMode(false);
                        }}
                      />
                    ) : (
                      <Flex
                        g={4}
                        a="center"
                        onClick={() =>
                          isReportEditMode && setTitleEditMode(true)
                        }
                      >
                        <Typo type="body0" weight="m" multilineEllipsis={2}>
                          {analysis.title}
                        </Typo>
                        {isReportEditMode && (
                          <IconButton
                            outlined={false}
                            icon={<EditOutlined />}
                            sizeType="m"
                            onClick={() => setTitleEditMode(true)}
                          />
                        )}
                      </Flex>
                    )}
                  </Flex>
                </TooltipV2>
                {isReportEditMode && (
                  <IconButton
                    outlined={false}
                    sizeType="m"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      removeColumn(column.row_alias, column);
                    }}
                  />
                )}
              </Flex>
            </DashboardCardHeader>
            <VFlex f={1} className="content-wrapper">
              <VFlex
                padding="0 24px"
                overflow="auto"
                ref={chatMessageContainerRef}
              >
                {analysis.chat && (
                  <HeySynclyMessageDisplay
                    chat_id={analysis.chat.alias}
                    messages={analysis.chat.messages}
                    status={analysis.chat.status}
                    report_alias={report.alias}
                    answer={null}
                    onStudioItemClick={() => {}}
                    container={chatMessageContainerRef.current}
                    hideFeedbackCount
                    hideReason
                    hideQuestion
                    hideVote
                  />
                )}
              </VFlex>
            </VFlex>
            <Flex padding="0px 24px 18px 24px" g={8} a="center">
              <Typo type="label3">
                Updated on{' '}
                {getTimezoneDayjs(analysis.updated_at).format(
                  DATE_FORMAT.readableHour,
                )}
              </Typo>
            </Flex>
          </Style>
        </Transition>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const Style = styled(VFlex)`
  height: 100%;
  .content-wrapper {
    overflow: auto;
    padding: 24px 0px;
    flex: 1;
  }
`;

const DashboardCardHeader = styled(VFlex)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray250};
  border-radius: ${radius.token['3xl']}px;
  height: 88px;
`;

const DashboardCardHeaderReferenceSection = styled(Flex)`
  background-color: ${(props) => props.theme.colors.ND04};
  padding: 8px 16px;
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.ND08};
    color: ${(props) => props.theme.colors.primary100};
  }
`;

export default ReportAnalysisItem;
